import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Empty, Input, Modal, Select, Spin, Tag, message } from "antd";
import dayjs from "dayjs";
import { DownloadOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import {
  getStandardReports,
  generateReport,
  saveReport,
  getSavedReports,
  deleteSavedReport,
  ClientDropdownData,
  getReportFilterCountries,
  getAcountOwnerList,
} from "../../services/ApiService";
import "./Reports.css";

// Any user on the app can reach this page and download reports with
// their own filters -- confirmed with the user, 2026-10-28. Standard
// reports are pre-built (one per REPORT_REGISTRY entry on the backend,
// see services/report_service.py); "My Reports" are a user's own saved
// (report_type + filters) combinations, private to them.
const Reports = () => {
  const [standardReports, setStandardReports] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientOptions, setClientOptions] = useState([]);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);

  // The report currently open in the filter modal, or null -- works for
  // both a standard report (reportType + default filters) and a saved
  // report (reportType + its stored filters), so one modal/flow covers
  // both "Download" entry points.
  const [activeReport, setActiveReport] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  // "country_select" filter options -- re-fetched whenever the modal
  // opens and whenever its client_id filter changes, scoped to that
  // client (or every distinct country, with no client picked yet).
  // Confirmed with the user, 2026-10-29.
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);

  const fetchCountryOptions = async (clientId) => {
    setCountryLoading(true);
    try {
      const res = await getReportFilterCountries(clientId);
      if (res?.responseCode === 200) {
        setCountryOptions((res.data || []).map((c) => ({ value: c, label: c })));
      }
    } catch (error) {
      console.error("Failed to load countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [stdRes, savedRes, clientRes, ownerRes] = await Promise.all([
        getStandardReports(),
        getSavedReports(),
        ClientDropdownData(),
        getAcountOwnerList(),
      ]);
      if (stdRes?.responseCode === 200) setStandardReports(stdRes.data || []);
      if (savedRes?.responseCode === 200) setSavedReports(savedRes.data || []);
      if (clientRes?.responseCode === 200) {
        setClientOptions(
          (clientRes.client || []).map((c) => ({ value: c.client_id, label: c.client_name }))
        );
      }
      if (ownerRes?.responseCode === 200) {
        setAccountOwnerOptions((ownerRes.res || []).map((o) => ({ value: o.id, label: o.name })));
      }
    } catch (error) {
      console.error("Failed to load reports:", error);
      message.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const reportsByCategory = useMemo(() => {
    const groups = {};
    standardReports.forEach((r) => {
      (groups[r.category] = groups[r.category] || []).push(r);
    });
    return groups;
  }, [standardReports]);

  const openReport = (reportDef, initialFilters) => {
    setActiveReport(reportDef);
    const defaults = {};
    (reportDef.filters || []).forEach((f) => {
      defaults[f.key] = initialFilters?.[f.key] ?? f.default ?? undefined;
    });
    setFilterValues(defaults);
    if ((reportDef.filters || []).some((f) => f.type === "country_select")) {
      fetchCountryOptions(defaults.client_id);
    }
  };

  const closeReport = () => {
    setActiveReport(null);
    setFilterValues({});
    setCountryOptions([]);
  };

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    // Switching clients invalidates whichever country was picked for the
    // PREVIOUS client (it may not even exist for the new one) -- re-fetch
    // the dropdown scoped to the new client and clear the stale value,
    // confirmed with the user, 2026-10-29.
    if (key === "client_id" && activeReport?.filters?.some((f) => f.type === "country_select")) {
      setFilterValues((prev) => ({ ...prev, country: undefined }));
      fetchCountryOptions(value);
    }
  };

  // The backend always returns a Blob (responseType: "blob"), even for
  // an error -- a real .xlsx comes back as that spreadsheet mimetype; an
  // error response is still JSON, just wrapped in a Blob, so it has to
  // be read back out as text and parsed before we know which case this is.
  const handleDownload = async () => {
    if (!activeReport) return;
    setDownloading(true);
    try {
      const blob = await generateReport(activeReport.report_type, filterValues);
      if (blob?.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        message.error(parsed.responseMessage || "Failed to generate report");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${activeReport.report_type}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Report downloaded");
    } catch (error) {
      console.error("Failed to generate report:", error);
      message.error("Failed to generate report");
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!activeReport || !saveName.trim()) {
      message.error("Please enter a name for this report.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveReport(saveName.trim(), activeReport.report_type, filterValues);
      if (res?.responseCode === 200) {
        message.success("Report saved");
        setSaveModalOpen(false);
        setSaveName("");
        fetchAll();
      } else {
        message.error(res?.responseMessage || "Failed to save report");
      }
    } catch (error) {
      console.error("Failed to save report:", error);
      message.error("Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSaved = async (id) => {
    try {
      const res = await deleteSavedReport(id);
      if (res?.responseCode === 200) {
        message.success("Saved report deleted");
        setSavedReports((prev) => prev.filter((r) => r.id !== id));
      } else {
        message.error(res?.responseMessage || "Failed to delete saved report");
      }
    } catch (error) {
      console.error("Failed to delete saved report:", error);
      message.error("Failed to delete saved report");
    }
  };

  const openSavedReport = (saved) => {
    const reportDef = standardReports.find((r) => r.report_type === saved.report_type);
    if (!reportDef) {
      message.error("This report type is no longer available.");
      return;
    }
    openReport(reportDef, saved.filters);
  };

  const renderFilterControl = (filterDef) => {
    const value = filterValues[filterDef.key];
    if (filterDef.type === "client_select") {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={`All ${filterDef.label.toLowerCase()}s`}
          style={{ width: "100%" }}
          value={value}
          options={clientOptions}
          onChange={(v) => handleFilterChange(filterDef.key, v)}
        />
      );
    }
    if (filterDef.type === "country_select") {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          loading={countryLoading}
          placeholder="All Countries"
          style={{ width: "100%" }}
          value={value}
          options={countryOptions}
          onChange={(v) => handleFilterChange(filterDef.key, v)}
        />
      );
    }
    if (filterDef.type === "account_owner_select") {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={`Any ${filterDef.label.toLowerCase()}`}
          style={{ width: "100%" }}
          value={value}
          options={accountOwnerOptions}
          onChange={(v) => handleFilterChange(filterDef.key, v)}
        />
      );
    }
    if (filterDef.type === "month_select") {
      return (
        <DatePicker
          picker="month"
          format="YYYY-MM"
          allowClear
          style={{ width: "100%" }}
          placeholder="Current month"
          value={value ? dayjs(value, "YYYY-MM") : null}
          onChange={(_, dateString) => handleFilterChange(filterDef.key, dateString || undefined)}
        />
      );
    }
    if (filterDef.type === "select") {
      return (
        <Select
          allowClear
          placeholder={`Any ${filterDef.label.toLowerCase()}`}
          style={{ width: "100%" }}
          value={value}
          options={(filterDef.options || []).map((o) => ({ value: o, label: o }))}
          onChange={(v) => handleFilterChange(filterDef.key, v)}
        />
      );
    }
    return (
      <Input
        allowClear
        placeholder={filterDef.label}
        value={value || ""}
        onChange={(e) => handleFilterChange(filterDef.key, e.target.value)}
      />
    );
  };

  return (
    <div className="main-box">
      <Sidebar activeId={14} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title="Reports" />
          <div style={{ flex: 1, padding: "1.5rem", boxSizing: "border-box", overflowY: "auto" }}>
            <Spin spinning={loading}>
              <h3 className="reports-section-title">Standard Reports</h3>
              {Object.keys(reportsByCategory).length === 0 && !loading ? (
                <Empty description="No standard reports available" />
              ) : (
                Object.entries(reportsByCategory).map(([category, reports]) => (
                  <div key={category} className="reports-category-block">
                    <div className="reports-category-label">{category}</div>
                    <div className="reports-card-grid">
                      {reports.map((r) => (
                        <div key={r.report_type} className="reports-card" onClick={() => openReport(r, null)}>
                          <div className="reports-card-title">{r.label}</div>
                          <div className="reports-card-description">{r.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}

              <h3 className="reports-section-title" style={{ marginTop: "2rem" }}>
                My Reports
              </h3>
              {savedReports.length === 0 ? (
                <p className="reports-empty-note">
                  No saved reports yet -- open a standard report above, set your filters, and save it to reuse later.
                </p>
              ) : (
                <div className="reports-saved-list">
                  {savedReports.map((r) => (
                    <div key={r.id} className="reports-saved-row">
                      <div>
                        <div className="reports-saved-name">{r.name}</div>
                        <Tag>{r.report_label}</Tag>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <Button icon={<DownloadOutlined />} onClick={() => openSavedReport(r)}>
                          Open
                        </Button>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteSaved(r.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Spin>
          </div>
        </div>
      </main>

      <Modal
        title={activeReport?.label}
        open={!!activeReport}
        onCancel={closeReport}
        footer={[
          <Button key="save" icon={<SaveOutlined />} onClick={() => setSaveModalOpen(true)}>
            Save as My Report
          </Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
            Download
          </Button>,
        ]}
        width={520}
      >
        <p className="reports-modal-description">{activeReport?.description}</p>
        <div className="reports-filter-form">
          {(activeReport?.filters || []).map((f) => (
            <div key={f.key} className="reports-filter-field">
              <label>{f.label}</label>
              {renderFilterControl(f)}
            </div>
          ))}
          {(activeReport?.filters || []).length === 0 && (
            <p className="reports-empty-note">This report has no filters -- it always includes everything.</p>
          )}
        </div>
      </Modal>

      <Modal
        title="Save as My Report"
        open={saveModalOpen}
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSaveReport}
        okText="Save"
        confirmLoading={saving}
      >
        <Input
          placeholder="Name this report (e.g. 'Papaya Global Headcount')"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Reports;
